import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  useCreateAdjustment,
  type CreateAdjustmentInput,
} from "@/hooks/queries/useBudgetAdjustments";

/**
 * MS557: mid-period adjustment modal.
 *
 * Wireframes §5. Two entry points — the Dashboard pool widget's
 * "Adjust" link and the Settings → Budget "Adjust this period" button.
 * Both render this same modal. Submission writes a single immutable
 * row to company_budget_adjustments via useCreateAdjustment().
 *
 * Locked product decisions encoded here:
 *
 *   - INSERT-only. The modal never offers an edit/delete path; the
 *     audit log is the source of truth. If the buyer made a mistake,
 *     they enter a counter-adjustment (e.g. they added $500 too much,
 *     they enter -$500 to net out). MS552 §5 + MS550 §3.
 *
 *   - Two kinds — one_time vs permanent. one_time changes this
 *     period only; permanent is the audit-trail anchor for a
 *     monthly_budget bump applied via Settings → Budget. This modal
 *     captures the *audit row* for both; updating monthly_budget
 *     itself is left to Settings (the modal surfaces a nudge for the
 *     permanent path so the buyer knows the recurring template needs
 *     a separate save).
 *
 *   - Negative amounts allowed. Reducing the pool is rare but
 *     supported — the buyer types "-500" or flips the kind via the
 *     sign of the input. We render a clear "Reduce by" mode when the
 *     amount is negative so the buyer can't be surprised by a
 *     sign-error.
 *
 *   - Never blocks. Even if the adjustment drives capacity negative
 *     (e.g. removing $5,000 from a $3,000 pool with $2,000 already
 *     spent), the INSERT proceeds. The widget shows the negative
 *     remaining via its existing tone="warn" Stat — see DashboardPoolWidget.
 *     Per MS552 §6: "warn but never block."
 *
 *   - Cents-only maths on submit. The input is dollars (the buyer
 *     thinks in $); the mutation gets `Math.round(dollars * 100)` so
 *     no float drift. Mirrors saveBudget's ceiling conversion.
 *
 * The component takes the active period as a prop rather than reading
 * it itself so the caller stays the source of truth (both entry points
 * already have the period loaded via useCurrentBudgetPeriod).
 */

export interface BudgetAdjustmentModalProps {
  /** Controlled open state — caller owns this so the entry point's
   *  button + dialog state stay coupled. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The active period the adjustment lands on. Required — modal
   *  refuses to render when null (the caller's entry-point button
   *  should be disabled in that state, but we guard here too). */
  periodId: string | null;
  /** Owning company for the audit row. */
  companyId: string | null;
  /** Profile id of the actor doing the adjusting (created_by). Null
   *  acceptable — the column is nullable in the schema (FK SET NULL
   *  on profile deletion). */
  createdBy: string | null;
  /** Optional contextual subtitle the caller can pass — e.g. "May
   *  2026 · $1,860 spent of $3,000". Render under the modal title to
   *  give the buyer a checkpoint before they commit. Falls back to a
   *  generic title when omitted. */
  periodSubtitle?: string;
}

export function BudgetAdjustmentModal({
  open,
  onOpenChange,
  periodId,
  companyId,
  createdBy,
  periodSubtitle,
}: BudgetAdjustmentModalProps) {
  // Controlled inputs. Amount is a string so the input stays
  // round-trip-safe (leading zeros, partial typing, "-" sign for
  // reductions). Kind defaults to "one_time" — the more common case
  // per wireframes §5.
  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState<"one_time" | "permanent">("one_time");
  const [reason, setReason] = useState("");

  const createMutation = useCreateAdjustment();

  // Reset state every time the modal closes so the next open starts
  // clean. Without this, a buyer who cancels mid-typing would see
  // their stale input on the next open — confusing rather than
  // helpful. Also clears any client-side validation state.
  useEffect(() => {
    if (!open) {
      setAmount("");
      setKind("one_time");
      setReason("");
    }
  }, [open]);

  // Parse the dollars input. Allow:
  //   - leading "-" for reductions
  //   - decimals (the input itself enforces step=0.01)
  //   - empty string → no submission
  // Returns null for "unparseable" so the submit button can disable.
  const parsedDollars = useMemo<number | null>(() => {
    const trimmed = amount.trim();
    if (trimmed === "" || trimmed === "-") return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    if (n === 0) return null; // zero adjustment is a no-op; block to save a roundtrip
    return n;
  }, [amount]);

  const amountCents = useMemo<number | null>(() => {
    if (parsedDollars === null) return null;
    return Math.round(parsedDollars * 100);
  }, [parsedDollars]);

  // Submit guard: amount present + parses + entry-point context
  // (companyId + periodId) available. The mutation handles its own
  // server-side guards (RLS, FK).
  const canSubmit =
    amountCents !== null &&
    Boolean(periodId) &&
    Boolean(companyId) &&
    !createMutation.isPending;

  const isReduction = amountCents !== null && amountCents < 0;

  const handleSubmit = async () => {
    if (!canSubmit || amountCents === null || !periodId || !companyId) {
      return;
    }
    const input: CreateAdjustmentInput = {
      companyId,
      periodId,
      amountCents,
      kind,
      reason: reason.trim() === "" ? null : reason.trim(),
      createdBy,
    };
    try {
      await createMutation.mutateAsync(input);
      // Confirmation toast — different copy per kind so the buyer
      // sees their intent reflected. Dollar formatting matches the
      // DashboardPoolWidget Stat helper (en-AU locale, whole-dollar
      // rounding for headline copy; the audit list shows full cents).
      const absDollars = Math.abs(amountCents) / 100;
      const formatted = absDollars.toLocaleString("en-AU", {
        maximumFractionDigits: 2,
      });
      if (kind === "one_time") {
        if (isReduction) {
          toast.success(`Reduced this period's budget by $${formatted}`);
        } else {
          toast.success(`Added $${formatted} to this period only`);
        }
      } else {
        if (isReduction) {
          toast.success(
            `Logged a $${formatted} reduction. Update your recurring budget in Settings → Budget to apply it going forward.`,
          );
        } else {
          toast.success(
            `Logged a $${formatted} top-up as permanent. Update your recurring budget in Settings → Budget to apply it going forward.`,
          );
        }
      }
      onOpenChange(false);
    } catch (err) {
      // Don't surface the raw supabase error (RLS denial, FK miss, schema
      // cache, etc.). The buyer can't act on it, so log the real cause for
      // Sentry and show a plain, calm failure instead.
      console.error("[BudgetAdjustmentModal] adjustment save failed:", err);
      toast.error("We couldn't save that change. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="budget-adjustment-modal"
      >
        <DialogHeader>
          <DialogTitle>Adjust this period&rsquo;s budget</DialogTitle>
          {periodSubtitle ? (
            <DialogDescription data-testid="budget-adjustment-subtitle">
              {periodSubtitle}
            </DialogDescription>
          ) : (
            <DialogDescription>
              Top up or reduce the current period&rsquo;s pool. Every
              change lands in the audit trail.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Amount input — dollars, signed. Caption tells the buyer
              the sign convention (negative for reductions). */}
          <div>
            <Label
              htmlFor="budget-adjustment-amount"
              className="text-navy font-medium"
            >
              Amount
            </Label>
            <div className="mt-1 relative w-44">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-charcoal/55">
                $
              </span>
              <Input
                id="budget-adjustment-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                placeholder="500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
                aria-label="Adjustment amount (AUD; negative to reduce)"
                data-testid="budget-adjustment-amount-input"
              />
            </div>
            <p className="mt-1 text-xs text-charcoal/55">
              Use a negative number to reduce the pool.
              {isReduction
                ? " You're reducing this period."
                : amountCents !== null
                  ? " You're adding to this period."
                  : ""}
            </p>
          </div>

          {/* Kind radio — one_time vs permanent. Each option's label
              names the consequence in plain language; the caption
              under "permanent" warns the buyer they also need to
              update the recurring template in Settings → Budget. */}
          <div>
            <Label className="text-navy font-medium">Apply this change</Label>
            <RadioGroup
              value={kind}
              onValueChange={(v) => setKind(v as "one_time" | "permanent")}
              className="mt-2 space-y-2"
              aria-label="Adjustment scope"
            >
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors cursor-pointer ${
                  kind === "one_time"
                    ? "border-coral bg-coral/5"
                    : "border-charcoal/15 hover:border-charcoal/30"
                }`}
              >
                <RadioGroupItem
                  value="one_time"
                  data-testid="budget-adjustment-kind-one_time"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-semibold text-navy">
                    One-time top-up this period only
                  </div>
                  <div className="mt-0.5 text-xs text-charcoal/65">
                    Adjusts this period&rsquo;s pool. Next period uses
                    your usual budget.
                  </div>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors cursor-pointer ${
                  kind === "permanent"
                    ? "border-coral bg-coral/5"
                    : "border-charcoal/15 hover:border-charcoal/30"
                }`}
              >
                <RadioGroupItem
                  value="permanent"
                  data-testid="budget-adjustment-kind-permanent"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-semibold text-navy">
                    Permanent change going forward
                  </div>
                  <div className="mt-0.5 text-xs text-charcoal/65">
                    Logs this period&rsquo;s adjustment now. Update
                    your recurring budget in Settings &rarr; Budget to
                    apply it from next period.
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Optional reason — free-text textarea. Surfaces in the
              audit drawer next to actor + timestamp. Trim + null on
              empty so the column matches "no reason" deterministically. */}
          <div>
            <Label
              htmlFor="budget-adjustment-reason"
              className="text-navy font-medium"
            >
              Reason{" "}
              <span className="font-normal text-charcoal/55">
                (optional)
              </span>
            </Label>
            <Textarea
              id="budget-adjustment-reason"
              className="mt-1 min-h-[60px]"
              placeholder="e.g. Off-cycle promotion gift for Priya"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              data-testid="budget-adjustment-reason-input"
            />
            <p className="mt-1 text-xs text-charcoal/55">
              Shows in the activity log so future-you remembers why.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
            data-testid="budget-adjustment-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            data-testid="budget-adjustment-submit"
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {createMutation.isPending
              ? "Saving…"
              : isReduction
                ? "Reduce budget"
                : "Add to budget"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
